import React, { useState } from 'react';
import { PROPERTIES, PROPERTY_CATEGORIES } from '../data/mockData';
import { MapPin, Maximize, ArrowRight, ChevronDown, Clock } from 'lucide-react';

const PropertyCard = ({ property, onViewDetails }) => (
  <div className="group relative bg-[#1a1a1a] rounded-lg overflow-hidden hover:transform hover:scale-[1.02] transition-all duration-500">
    {/* Image */}
    <div className="relative h-56 overflow-hidden">
      <img
        src={property.image}
        alt={property.name}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      {/* Badges */}
      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[calc(100%-24px)] z-10">
        <span className="px-2.5 py-1 bg-[#DAA520] text-black text-[10px] font-bold tracking-wider rounded-sm whitespace-nowrap">
          {property.badge}
        </span>
        <span className="px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white text-[10px] font-bold tracking-wider rounded-sm border border-white/20 whitespace-nowrap">
          {property.type}
        </span>
        {property.builder && (
          <span className="px-2.5 py-1 bg-black/60 backdrop-blur-sm text-[#DAA520] text-[10px] font-bold tracking-wider rounded-sm border border-[#DAA520]/30 whitespace-nowrap">
            {property.builder}
          </span>
        )}
      </div>
      {/* Price */}
      <div className="absolute bottom-3 left-3">
        <span className="text-[#DAA520] font-bold text-lg">{property.price}</span>
      </div>
    </div>

    {/* Content */}
    <div className="p-5">
      <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-[#DAA520] transition-colors">
        {property.name}
      </h3>
      <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
        <MapPin size={14} className="text-[#DAA520]" />
        {property.location}
      </div>
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <Maximize size={14} className="text-[#DAA520]" />
        {property.size}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-800">
        <button
          onClick={() => onViewDetails && onViewDetails(property.id)}
          className="flex items-center gap-2 text-[#DAA520] text-sm font-semibold tracking-wider hover:gap-3 transition-all duration-300"
        >
          VIEW DETAILS
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  </div>
);

const ComingSoon = ({ category }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-20 h-20 rounded-full bg-[#DAA520]/10 flex items-center justify-center mb-6">
      <Clock size={36} className="text-[#DAA520]" />
    </div>
    <h3 className="text-white text-xl font-semibold mb-3">
      {category} Coming Soon
    </h3>
    <p className="text-gray-400 text-sm max-w-md">
      We're curating the finest {category.toLowerCase()} for you. Stay tuned for exclusive listings that will be added shortly.
    </p>
  </div>
);

const FeaturedListings = ({ onViewProject }) => {
  const [activeCategory, setActiveCategory] = useState('apartments');
  const [showAll, setShowAll] = useState(false);

  const getDisplayProperties = () => {
    const props = PROPERTIES[activeCategory] || [];
    if (!showAll) {
      // Show only featured items initially (first 4 max)
      const featured = props.filter(p => p.featured === true);
      return featured.length > 0 ? featured : props.slice(0, 4);
    }
    return props;
  };

  const properties = getDisplayProperties();
  const totalCount = (PROPERTIES[activeCategory] || []).length;
  const canShowMore = properties.length < totalCount;
  const isEmpty = totalCount === 0;

  return (
    <section id="properties" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-2 text-xs font-semibold tracking-[0.2em] text-[#DAA520] border border-[#DAA520]/30 rounded-sm mb-4">
            OUR PORTFOLIO
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Exceptional Properties
          </h2>
          <div className="w-12 h-0.5 bg-[#DAA520] mx-auto mb-4" />
          <p className="text-gray-400 max-w-xl mx-auto">
            Handpicked luxury properties that represent the pinnacle of refined living
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          {PROPERTY_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                setShowAll(false);
              }}
              className={`px-6 py-3 text-sm font-semibold tracking-wider rounded-sm transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-[#DAA520] text-black'
                  : 'border border-gray-700 text-gray-400 hover:border-[#DAA520]/50 hover:text-[#DAA520]'
              }`}
            >
              {cat.label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Properties Grid or Coming Soon */}
        {isEmpty ? (
          <ComingSoon category={PROPERTY_CATEGORIES.find(c => c.id === activeCategory)?.label || activeCategory} />
        ) : (
          <>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${properties.length <= 3 ? 'lg:grid-cols-3 max-w-5xl mx-auto' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6`}>
              {properties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onViewDetails={onViewProject}
                />
              ))}
            </div>

            {/* View More */}
            {canShowMore && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setShowAll(true)}
                  className="inline-flex items-center gap-2 px-8 py-3 border border-[#DAA520] text-[#DAA520] font-semibold tracking-wider text-sm rounded-sm hover:bg-[#DAA520] hover:text-black transition-all duration-300"
                >
                  VIEW MORE
                  <ChevronDown size={16} />
                </button>
              </div>
            )}
            {showAll && totalCount > 4 && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setShowAll(false)}
                  className="inline-flex items-center gap-2 px-8 py-3 border border-gray-700 text-gray-400 font-semibold tracking-wider text-sm rounded-sm hover:border-[#DAA520]/50 hover:text-[#DAA520] transition-all duration-300"
                >
                  SHOW LESS
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedListings;
